import { CSL_TYPE_NAMES, type CslType } from './csl-type-names';

const VALID_CSL_TYPES: ReadonlySet<string> = new Set(CSL_TYPE_NAMES);

/**
 * Non-CSL item-type names (CrossRef / OpenAlex) mapped to their CSL-JSON
 * standard equivalent. CSL-JSON is the canonical form used by the schemas and
 * citeproc; external sources carry their own vocabularies.
 *
 * Of CrossRef's 30 work types, only `book`, `dataset`, `report`, and `standard`
 * are already valid CSL and pass through unchanged. The structural ones map
 * below; anything still unrecognised (CrossRef `journal`/`component`/`grant`/
 * `other`, OpenAlex `paratext`/`letter`/`editorial`/…) falls back to the CSL
 * generic `document` via `normalizeCslType`, so the editor always has a schema.
 *
 * Each row notes the date checked and the source. Sources:
 * `citation.js` = https://github.com/citation-js/citation-js (plugin-doi);
 * `crossref` = https://api.crossref.org/types; `openalex` = https://developers.openalex.org.
 */
export const CSL_TYPE_ALIASES: Readonly<Record<string, CslType>> = {
  'journal-article': 'article-journal', // 2026-06-12 citation.js
  'book-chapter': 'chapter', // 2026-06-12 citation.js
  'proceedings-article': 'paper-conference', // 2026-06-12 citation.js
  'posted-content': 'manuscript', // 2026-06-12 citation.js
  dissertation: 'thesis', // 2026-06-12 citation.js
  'book-section': 'chapter', // 2026-06-12 crossref (book-chapter synonym)
  'book-part': 'chapter', // 2026-06-12 crossref (book-chapter synonym)
  'book-track': 'chapter', // 2026-06-12 crossref (book-chapter synonym)
  'edited-book': 'book', // 2026-06-12 crossref
  monograph: 'book', // 2026-06-12 crossref
  'reference-book': 'book', // 2026-06-12 crossref
  'book-set': 'book', // 2026-06-12 crossref
  'book-series': 'book', // 2026-06-12 crossref
  proceedings: 'book', // 2026-06-12 crossref
  'proceedings-series': 'book', // 2026-06-12 crossref
  database: 'dataset', // 2026-06-12 crossref
  'peer-review': 'review', // 2026-06-12 crossref
  'reference-entry': 'entry-encyclopedia', // 2026-06-12 crossref
  'report-component': 'report', // 2026-06-12 crossref
  'report-series': 'report', // 2026-06-12 crossref
  preprint: 'article', // 2026-06-12 openalex (citation.js routes preprints to article)
};

/**
 * Normalize a possibly-external CSL type to the CSL-JSON standard:
 *   - a known alias  → its mapped CSL type
 *   - an already-valid CSL type → unchanged
 *   - anything else  → `document` (the CSL generic item type)
 *
 * The `document` fallback means an unknown/untranslatable type (e.g. OpenAlex
 * `paratext`, `letter`, `editorial`) still resolves to a real schema, so the
 * editor never dead-ends on "type not supported".
 */
export function normalizeCslType(type: string): CslType {
  const alias = CSL_TYPE_ALIASES[type];
  if (alias) return alias;
  if (VALID_CSL_TYPES.has(type)) return type as CslType;
  return 'document';
}
